// The hand: the cards you hold, fanned along the bottom of the screen
// (PLAN-UI §28.3, U7; screen-mock `#hand`).
//
// **Cards left the shelf because a card is a thing you hold, not a row in a
// panel.** In the dock they were a wrapped list inside a column headed YOUR
// HAND, three clicks of scrolling deep on a short window, and they read as
// more interface — the one part of the game that is literally a hand of cards
// looked exactly like the material breakdown beside it. Down here they are
// objects lying half off the bottom edge, they fan when the pointer reaches
// them, and nothing has to be scrolled to.
//
// **Bottom centre, registered `bottom`.** The hand is the new bottom of the
// screen (relocation map, "Hand + pill"), so it is the piece of chrome that
// says how deep the bottom edge is covered. The container's rect must not
// change on hover, which is why the poses are transforms *inside* it: a zone
// that grew by a card's whole reach every time the pointer crossed it would
// move the camera.
//
// **The zone takes no pointer and the seats do.** A 500×122 catcher across the
// bottom middle of the board would eat clicks meant for the squares under it,
// which is the whole complaint that killed the board-anchored action bar at
// U6. So `pointer-events` is `none` on the frame and `auto` on the two things
// that are actually controls.
//
// **A card is not its own hit area, and that is the load-bearing bit.** The
// control is a *seat* (`fanSeats`) — the box the layout says the card's
// visible sliver occupies — and the drawing moves inside it. Hung on the card
// itself, a hover lift takes the card out from under a stationary pointer,
// which un-hovers it, which drops it back under the pointer — a strobe at the
// frame rate, and one Playwright reports as "element is not stable" forever,
// since it waits for two identical bounding boxes before it will click.
// Measured, not predicted: the first draft did exactly this and the run smoke
// timed out on the first card it tried to play. A seat is the card's *resting*
// sliver and a hover never moves one sideways — it only grows it upward — so
// sifting along the fan lights every card in turn in both directions.
//
// Explicit props, no store reads: `Hotbar`'s reasoning, for `Hotbar`'s reason —
// the view is the session's, memoised per revision, and a component that
// fetched its own state would rebuild the whole hand on every render of
// everything above it. It is typed on `src/api` alone (day 8).

import { type KeyboardEvent, useState } from "react";

// Not a store read, which this file avoids on purpose: the engine is told
// what happened, it never says what is on the board.
import { sound } from "src/client/ai/audio/engine";

import {
  CARD_H,
  CARD_SUNK,
  CARD_W,
  FAN_HEIGHT,
  PERCH_H,
  PERCH_W,
  type CardPose,
  type SeatBox,
  fanLayout,
  fanSeats,
  fanWidth
} from "./hand";
import type { CardGlyph, HandCard, HandView, OptionId } from "src/api";
import {
  CardArt,
  CardCost,
  CardPlate,
  CardText,
  GameCard,
  HoverCard,
  Icon,
  motion,
  TOKENS,
  styled,
  useChromeRect
} from "./kit";

export interface HandProps {
  /** The cards, and the loose-artifact sentence. `SessionView.hand`, non-null. */
  view: NonNullable<HandView>;
  /** A card clicked: `session.choose`. */
  onChoose(option: OptionId): void;
}

// Static: nothing here moves per frame. The poses are `style` on the cards.
const Zone = styled("div")(({ theme }) => ({
  position: "fixed",
  left: "50%",
  bottom: 0,
  transform: "translateX(-50%)",
  zIndex: theme.kit.z.chrome,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.kit.space.sm,
  // The frame is geometry, not a target. See the file header.
  pointerEvents: "none"
}));

/** The fan's own box: the part of a card that is on screen at rest. */
const Fan = styled("div")({ position: "relative", height: FAN_HEIGHT });

/**
 * A card's place in the fan: the control, and the only thing that is clicked.
 *
 * **The box is the card's resting sliver and never moves sideways**
 * (`fanSeats`): the drawing parts, lifts and grows over it, taking no pointer,
 * so the pointer just right of a raised card's middle lights the next card —
 * which is what makes sifting the hand smooth in both directions. Only the
 * height changes with the hover, and it is written through `style`.
 *
 * It carries the ids, the role and — through the drawing inside it — the
 * card's words, so `textContent` on `card-<id>` still reads the whole card.
 * `run-smoke.js` reads the picking chip off exactly that.
 *
 * The perch's `bottom` is negative, so the foot of every card is under the
 * screen edge — the mock's `#hand { bottom: -64px }` scaled to the kit's card.
 * A seat's own bottom is that resting foot line **in every state**: a seat
 * that rose with the hand would leave a pointer resting near a card's foot,
 * which is the strobe the file header is about.
 */
const Seat = styled("div")({
  position: "absolute",
  pointerEvents: "auto",
  cursor: "pointer"
});

/**
 * The room a card rises into — what the hover card hangs off.
 *
 * **Not the seat, or a `top` card lands on the raised card's own plate.** The
 * kit's rule is that a card explains a control and never stands in front of
 * it, and the control here rises `CARD_REACH` after Popper measured its
 * anchor. This box is that reach plus `HOVER_GAP` of clear air, so `top`
 * leaves a gap above the raised card rather than landing flush on it
 * (designer, U7 review).
 *
 * Constant per index: it is centred on the card's *resting* centre and nothing
 * about the hand's state is in it, so the anchor never moves under an open
 * card. Transparent to the pointer, so the board above a resting hand is still
 * board: it is the seat's events that bubble through this, which is all
 * `HoverCard` needs of it.
 */
const Perch = styled("div")({
  position: "absolute",
  left: "50%",
  bottom: -CARD_SUNK,
  width: PERCH_W,
  height: PERCH_H,
  marginLeft: -PERCH_W / 2,
  pointerEvents: "none"
});

/**
 * The drawing, which is the half that moves.
 *
 * Transparent to the pointer, so every event is the seat's and a card sliding
 * about cannot change what is being hovered. The transform is written through
 * `style` — a pose is a per-state value and `sx` would mint an emotion class
 * per distinct one, the standing rule here and the one `PieceOverlays` pays
 * for sixty times a second.
 *
 * **Picking wins the border and rarity keeps the glow**, which is the shelf's
 * own ruling moved across: a card being aimed is a thing happening *now* and
 * rarity is a standing fact about it, so the transient state takes the outline
 * (`GameCard`'s border) and the rarity ring stays in the shadow underneath.
 */
const Card = styled(GameCard, {
  shouldForwardProp: (prop: string) => prop !== "picking"
})<{ picking?: boolean }>(({ theme, picking }) => ({
  position: "absolute",
  bottom: 0,
  // **`border-box`, or the card is 15px bigger than the fan thinks it is** —
  // `GameCard` carries 5px of padding and a 2.5px border, and its own 138×196
  // is the box the seats and `CARD_REACH` are measured in. Measured: the hover
  // card's clearance came out at 7px instead of 22.
  boxSizing: "border-box",
  width: CARD_W,
  height: CARD_H,
  pointerEvents: "none",
  display: "flex",
  flexDirection: "column",
  textAlign: "left",
  ...(picking ? { borderColor: theme.kit.color.brand } : null),
  ...motion(["transform"], [], TOKENS.motion.duration.draw)
}));

/**
 * The rule under the picture. Takes what the plate and the art leave.
 *
 * **Nothing is clipped** (designer, U7 review): a card that ends mid-sentence
 * is a card the player has to hover to read, which is the failure the terse
 * `short` line exists to prevent rather than a thing to paper over. The face
 * says `card.short` and the hover card says the whole of `card.text`.
 */
const Text = styled(CardText)({ flex: 1, minHeight: 0 });

/** The art window never gives ground to a long name or a long sentence. */
const Art = styled(CardArt)({ flexShrink: 0 });

/** A body line on a hover card, in the secondary ink. */
const Line = styled("div")(({ theme }) => ({
  fontFamily: theme.kit.font.body,
  fontSize: 12,
  lineHeight: 1.45,
  color: theme.kit.color.inkDim
}));

/** What kind of card it is, and whether it wants a square. */
const Note = styled(Line)(({ theme }) => ({ color: theme.kit.color.ink }));

function Glyph({ glyph }: { glyph: CardGlyph }) {
  return glyph.kind === "icon" ? <Icon name={glyph.name} size={40} /> : <>{glyph.text}</>;
}

export function Hand({ view, onChoose }: HandProps) {
  const root = useChromeRect("hand", "bottom");
  const [hovered, setHovered] = useState<number | null>(null);

  /**
   * Light a card, and hear the fan move.
   *
   * **Only when the hovered card actually changes** — a re-enter of the seat
   * already lit is the pointer jittering inside one box, and the hand is not
   * moving. A focus counts as an enter, so sifting on the keyboard sounds too.
   *
   * Read off `hovered` rather than from inside the updater: StrictMode calls a
   * state updater twice, and a sound is not something to do twice.
   */
  const enter = (index: number) => {
    if (hovered !== index) sound.playUi("card-fan");
    setHovered(index);
  };

  const { cards } = view;
  // **Nothing to say, nothing on screen** — and so nothing registered, which
  // is the half that matters: an empty zone still has a rect, and a rect on
  // the bottom edge is a claim that the bottom edge is covered.
  if (cards.length === 0) return null;

  // The hand opens as one object: any card lit means every card lifts.
  const poses = fanLayout(cards.length, hovered, hovered !== null);
  const seats = fanSeats(cards.length, hovered, hovered !== null);

  return (
    <Zone ref={root} data-testid="hand">
      {cards.length > 0 && (
        <Fan style={{ width: fanWidth(cards.length) }}>
          {cards.map((card, index) => (
            <Held
              key={card.instance}
              card={card}
              pose={poses[index]}
              seat={seats[index]}
              onEnter={() => enter(index)}
              onLeave={() => setHovered((was) => (was === index ? null : was))}
              onPlay={() => onChoose(card.option)}
            />
          ))}
        </Fan>
      )}
    </Zone>
  );
}

function Held({
  card,
  pose,
  seat,
  onEnter,
  onLeave,
  onPlay
}: {
  card: HandCard;
  pose: CardPose;
  /** What can be seen of this card, which is what can be clicked. */
  seat: SeatBox;
  onEnter(): void;
  onLeave(): void;
  onPlay(): void;
}) {
  // Everything the DOM needs is an offset from the perch, which is pinned to
  // the card's resting centre: the seat off the perch's left edge, and the
  // drawing off the seat it is now a child of.
  const seatLeft = seat.left - pose.centre + PERCH_W / 2;
  const cardLeft = pose.centre - seat.left - CARD_W / 2;
  return (
    // **A hover card, never a `title`** (designer ruling, U6 review). The card
    // itself carries the name and the rule in 10.8px type inside 128px; the
    // things it cannot fit — what rarity that border means, and that this one
    // is going to ask for a square — are what this says.
    //
    // `top`, because the fan is a row: a card opening to the side would land
    // on the neighbour it overlaps. Up is also the only free direction, the
    // hand being against the bottom edge.
    <HoverCard
      title={card.name}
      placement="top"
      data-testid={`hand-card-${card.id}`}
      card={
        <>
          {/* The whole account. The face carries `short`, which is the terse
              half of the same sentence (U7 review, item 4). */}
          <Line>{card.text}</Line>
          <Note>{card.rarityName}</Note>
          {card.targeted && <Note>Needs a target</Note>}
        </>
      }
    >
      <Perch
        // Constant per index: nothing about the hand's own state is in here,
        // which is what makes the anchor stable while a card is open.
        style={{ transform: `translateX(${pose.centre}px)`, zIndex: pose.z }}
      >
        <Seat
          data-testid={`card-${card.id}`}
          data-rarity={card.rarity}
          role="button"
          tabIndex={0}
          aria-label={card.name}
          style={{
            left: seatLeft,
            bottom: seat.bottom,
            width: Math.max(0, seat.right - seat.left),
            height: seat.top - seat.bottom
          }}
          onPointerEnter={onEnter}
          onPointerLeave={onLeave}
          onFocus={onEnter}
          onBlur={onLeave}
          onClick={onPlay}
          onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            onPlay();
          }}
        >
          <Card
            rarity={card.rarity}
            picking={card.picking}
            // Placed on the perch's own middle — through the seat, which is a
            // sliver of it — and then moved by the pose. The drawing overhangs
            // its seat on both sides and takes no pointer, so what is
            // clickable stays the sliver however the card above it is drawn.
            style={{
              left: cardLeft,
              transform: `translate(${pose.x - pose.centre}px, ${-pose.lift}px) rotate(${pose.rotate}deg) scale(${pose.scale})`
            }}
          >
            {/* **The one word on a card, and only while it is aiming.**
                "PLAY" and "NEEDS A TARGET" were captions on a thing whose
                whole job is to be clicked — fewer words is a standing
                instruction (U3 ruling) — but "pick a piece" is a *state*: the
                board is waiting and the card is the only thing that can say
                so. `run-smoke.js` reads it off this element. */}
            {card.picking && <CardCost>pick a piece</CardCost>}
            <CardPlate>{card.name}</CardPlate>
            <Art>
              <Glyph glyph={card.glyph} />
            </Art>
            <Text>{card.short}</Text>
          </Card>
        </Seat>
      </Perch>
    </HoverCard>
  );
}
